import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getMaxAttachmentByteCountForEmailConfigurations } from '../../../../extViewmodels/Utils';
import { EventLogger } from '../../../../models/Logging';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	AttachmentsToBeUploadedViewModel,
	IFileAttachment,
	IImpersonationContext,
	IInProgressFileAttachment,
} from '../../../../viewmodels/AppViewModels';
import { Checkbox } from '../../../components-v2/Checkbox';
import { baseStyleSheet } from '../../../styles/styles';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	emailAttachments?: AttachmentsToBeUploadedViewModel<File>;
	impersonationContext?: IImpersonationContext;
	inProgressAttachments?: IInProgressFileAttachment[];
	onRequestRemoveSavedEmailAttachment?(savedAttachment: IFileAttachment): Promise<void>;
	onToggleSendAttachmentsAsLink?(value: boolean): void;
	readonly?: boolean;
	savedAttachments?: IFileAttachment[];
	sendAttachmentsAsLink?: boolean;
}

function EmailAttachment({
	size,
	exceedsMaxByteCount,
	name,
	onRemoveClicked,
	pending,
}: {
	size: number;
	exceedsMaxByteCount?: boolean;
	name: string;
	onRemoveClicked?(e: React.MouseEvent<HTMLElement>): void;
	pending?: boolean;
}) {
	const fileSizeStringValue = getFileSizeStringValue(size);
	return (
		<div
			className={`${css(
				styleSheet.item,
				exceedsMaxByteCount ? styleSheet.itemError : null,
				pending ? styleSheet.itemPending : null
			)}`}
			key={name}
		>
			<span className={css(baseStyleSheet.truncateText, styleSheet.itemName)} title={name}>
				{name}
			</span>
			<span className={css(styleSheet.itemSize)} title={fileSizeStringValue}>
				{fileSizeStringValue}
			</span>
			{pending ? (
				<LoadingSpinner type='tiny' />
			) : onRemoveClicked ? (
				<DeprecatedCloseButton styles={[styleSheet.itemRemove]} onClick={onRemoveClicked} />
			) : null}
		</div>
	);
}

const TOGGLE_CHECKBOX_ID = 'email-attachments-send-as-link';
const TWENTY_MB = 20 * 1024 * 1024;

export const EmailAttachments = observer(function EmailAttachmentsBase({
	className,
	emailAttachments,
	impersonationContext,
	inProgressAttachments,
	onRequestRemoveSavedEmailAttachment,
	onToggleSendAttachmentsAsLink,
	readonly,
	savedAttachments,
	sendAttachmentsAsLink,
}: IProps) {
	const userSession = useUserSession();
	const onRemoveSavedEmailAttachment = (savedAttachment: IFileAttachment) => (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();
		onRequestRemoveSavedEmailAttachment?.(savedAttachment);
	};

	const onRemoveClicked = (file: File) => (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		e.preventDefault();

		emailAttachments.remove([file]);
		EventLogger.logInput('EmailAttachments', `Remove`, 'Click', {
			size: file.size,
			type: file.type,
		});
	};

	const totalCount =
		(emailAttachments?.attachments?.length ?? 0) +
		(savedAttachments?.length ?? 0) +
		(inProgressAttachments?.length ?? 0);

	if (totalCount === 0) {
		return null;
	}

	const maxBytes = getMaxAttachmentByteCountForEmailConfigurations(
		impersonationContext?.account?.emailProviderConfigurations || userSession.account.emailProviderConfiguration
	);
	const totalBytes =
		(emailAttachments?.byteCount ?? 0) +
		(savedAttachments || []).reduce((count, x) => count + x.fileSize, 0) +
		(inProgressAttachments?.reduce((count, x) => count + x.fileSize, 0) ?? 0);
	const hasExceededMaxByteCount = sendAttachmentsAsLink
		? totalBytes > TWENTY_MB
		: emailAttachments?.hasExceededMaxByteCount || totalBytes > maxBytes;
	return (
		<div className={`email-attachments ${className || ''} ${css(styleSheet.container)}`}>
			{!!hasExceededMaxByteCount && (
				<div className={css(styleSheet.error)}>
					{sendAttachmentsAsLink
						? `You've exceeded the secure file link size limit of ${getFileSizeStringValue(TWENTY_MB)}.`
						: `You've exceeded the attachment size limit of ${getFileSizeStringValue(maxBytes)}.`}
				</div>
			)}
			<div className={css(styleSheet.header)}>
				<span className={css(styleSheet.headerLabel)}>
					{totalCount} attachment{totalCount !== 1 ? 's' : ''}
				</span>
				{!!onToggleSendAttachmentsAsLink && (
					<div className={css(styleSheet.toggleRow)}>
						<Checkbox
							checked={!!sendAttachmentsAsLink}
							id={TOGGLE_CHECKBOX_ID}
							onCheckedChange={value => onToggleSendAttachmentsAsLink(value === true)}
						/>
						<label className={css(styleSheet.toggleLabel)} htmlFor={TOGGLE_CHECKBOX_ID}>
							Send as download link
						</label>
					</div>
				)}
			</div>
			<div className={css(styleSheet.content)}>
				{inProgressAttachments?.map(x => {
					return (
						<EmailAttachment
							key={x.uploadTaskId}
							name={x.fileName}
							onRemoveClicked={!readonly && x.id ? onRemoveSavedEmailAttachment(x as IFileAttachment) : undefined}
							pending={Boolean(x.uploadPromise)}
							size={x.fileSize}
						/>
					);
				})}
				{savedAttachments?.map(x => {
					return (
						<EmailAttachment
							key={x.id}
							name={x.fileName}
							onRemoveClicked={onRemoveSavedEmailAttachment(x)}
							size={x.fileSize}
						/>
					);
				})}
				{emailAttachments?.attachmentsWithMetadata.map((x, i) => {
					return (
						<EmailAttachment
							exceedsMaxByteCount={sendAttachmentsAsLink ? false : x.exceedsMaxByteCount}
							key={`${x.file.name || ''}-${i}`}
							name={x.file.name}
							onRemoveClicked={!readonly && onRemoveClicked(x.file)}
							size={x.file.size}
						/>
					);
				})}
			</div>
			{!!sendAttachmentsAsLink && (
				<p className={css(styleSheet.infoText)}>
					Files will be replaced with a secure download link in the email body.
				</p>
			)}
		</div>
	);
});
